import { Alert, Button, Field, Input } from '@personachat/ui';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';

import { messageDe } from '../api/call';

/** Fenêtre de dialogue centrée : formulaires courts et confirmations. */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 520,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal__overlay" />
        <Dialog.Content className="modal" style={{ width: `min(${width}px, calc(100vw - 32px))` }} aria-describedby={undefined}>
          <div className="modal__header">
            <Dialog.Title className="modal__title">{title}</Dialog.Title>
            <Dialog.Close className="icon-btn" aria-label="Fermer">
              <X size={16} aria-hidden="true" />
            </Dialog.Close>
          </div>
          {description && <div className="modal__description">{description}</div>}
          {children && <div className="modal__body">{children}</div>}
          {footer && <div className="modal__footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Confirmation d'une action sensible (cahier des charges §4) : l'impact est
 * affiché, un motif peut être exigé, une suppression demande de retaper
 * l'e-mail ou le code. Le message de l'API s'affiche en cas d'échec.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  children,
  confirmLabel,
  tone = 'primary',
  motif = false,
  retaper,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Impact de l'action, en clair. */
  children: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  /** true : champ « motif » obligatoire (3 caractères au moins), passé à onConfirm. */
  motif?: boolean;
  /** Texte à retaper à l'identique pour confirmer (e-mail, code). */
  retaper?: string;
  onConfirm: (motif: string) => Promise<void>;
}) {
  const id = useId();
  const [texteMotif, setTexteMotif] = useState('');
  const [saisie, setSaisie] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (open) {
      setTexteMotif('');
      setSaisie('');
      setErreur(null);
    }
  }, [open]);

  const motifValide = !motif || texteMotif.trim().length >= 3;
  const retapeValide = !retaper || saisie.trim().toLowerCase() === retaper.trim().toLowerCase();

  const valider = async (e: FormEvent) => {
    e.preventDefault();
    if (!motifValide || !retapeValide) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await onConfirm(texteMotif.trim());
      onOpenChange(false);
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(o) => !envoi && onOpenChange(o)} title={title}>
      <form className="stack" onSubmit={valider}>
        <div className="confirm__impact">{children}</div>
        {motif && (
          <Field id={`${id}-motif`} label="Motif" hint="Obligatoire, inscrit au journal d’audit.">
            <Input
              id={`${id}-motif`}
              value={texteMotif}
              onChange={(e) => setTexteMotif(e.target.value)}
              maxLength={300}
              autoFocus
              placeholder="Ex. : demande de l’élève par e-mail du 29/09"
            />
          </Field>
        )}
        {retaper && (
          <Field id={`${id}-retape`} label={`Pour confirmer, tapez « ${retaper} »`}>
            <Input id={`${id}-retape`} value={saisie} onChange={(e) => setSaisie(e.target.value)} autoComplete="off" autoFocus={!motif} />
          </Field>
        )}
        {erreur && <Alert>{erreur}</Alert>}
        <div className="modal__footer modal__footer--inline">
          <Button onClick={() => onOpenChange(false)} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant={tone} loading={envoi} disabled={!motifValide || !retapeValide}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
